import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, BarChart3, ShoppingBag, LayoutTemplate, Users, Settings,
  Wallet, BookOpen, Cpu, Eye, CheckCircle2, XCircle, Hammer, ShoppingCart,
} from 'lucide-react';
import { api, resolveAsset } from '../../api/client';
import { useAuth } from '../../state/auth';
import { useT } from '../../i18n';
import DuplicateStoryModal from '../../components/DuplicateStoryModal';

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('ar', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return new Date(d).toISOString().slice(0, 16).replace('T', ' ');
  }
}

const PAGE_SIZE = 25;

export default function AdminDashboard() {
  const t = useT();
  const VARIANT_AR = { softcover: t.adminVariantSoftcover, hardcover: t.adminVariantHardcover, digital: 'PDF' };
  const STATUS_AR = {
    previewing: t.adminStatusPreviewing,
    preview_ready: t.adminStatusPreviewReady,
    finalizing: t.adminStatusFinalizing,
    finalized: t.adminStatusFinalized,
    iterating: t.adminStatusIterating,
    locked_for_print: t.adminStatusLockedForPrint,
    failed: t.adminStatusFailed,
    unknown: t.adminStatusUnknown,
  };
  // Editors share the dashboard but never the stats: no summary widgets, no
  // stats/templates/users/settings tiles. The /stats endpoint is admin-only, so
  // editors don't fetch it and the page renders straight to the previews table.
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [previews, setPreviews] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [search, setSearch] = useState('');
  // Debounced copy of `search` — only this drives the fetch.
  const [query, setQuery] = useState('');
  // Zero-based page index for the previews table; the server paginates via
  // skip/limit (see GET /api/admin/previews).
  const [page, setPage] = useState(0);
  // Pending delete confirmation: { id, label, error? } | null.
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Row being duplicated: { id, label, status, isTest } | null.
  const [duplicating, setDuplicating] = useState(null);
  const [working, setWorking] = useState(false);
  // Which row's demo link was just copied (shows a transient "copied ✓" label).
  const [copiedToken, setCopiedToken] = useState(null);

  // The shareable, watermarked-images demo URL for a story. A clean page route
  // (/demo/:token) that works the same in dev and prod; absolute so it can be
  // pasted straight into WhatsApp/etc.
  const demoUrl = (shareToken) => `${window.location.origin}/demo/${shareToken}`;
  const copyDemoLink = (shareToken) => {
    const url = demoUrl(shareToken);
    const done = () => { setCopiedToken(shareToken); setTimeout(() => setCopiedToken(null), 1600); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(done);
    else done();
  };

  const loadPreviews = useCallback(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(page * PAGE_SIZE) });
    if (showTest) params.set('includeTest', 'true');
    if (query) params.set('q', query);
    api.get(`/api/admin/previews?${params.toString()}`).then(setPreviews).catch(() => {});
  }, [showTest, page, query]);

  // Debounce the search box and reset to the first page on a new term.
  useEffect(() => {
    const id = setTimeout(() => { setPage(0); setQuery(search.trim()); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  // Stats are filtered by the "show test data" toggle but not by page, so they
  // re-fetch only when the toggle changes — not on every page step. Admin-only:
  // editors get a 403 here, so skip the fetch for them entirely.
  useEffect(() => {
    if (!isAdmin) return;
    api.get(`/api/admin/stats${showTest ? '?includeTest=true' : ''}`).then(setStats).catch(() => {});
  }, [showTest, isAdmin]);

  // The recent-previews table re-fetches whenever the filter or page changes.
  useEffect(() => { loadPreviews(); }, [loadPreviews]);

  const runDelete = async () => {
    if (!confirmDelete) return;
    setWorking(true);
    try {
      await api.delete(`/api/admin/previews/${confirmDelete.id}`);
      setConfirmDelete(null);
      loadPreviews();
      // Refresh the counters so the preview/story tallies reflect the removal
      // (admins only — editors have no stats panel).
      if (isAdmin) api.get(`/api/admin/stats${showTest ? '?includeTest=true' : ''}`).then(setStats).catch(() => {});
    } catch (e) {
      setConfirmDelete((c) => (c ? { ...c, error: e.message || t.adminDeleteFailed } : c));
    } finally {
      setWorking(false);
    }
  };

  // Only admins block on the stats fetch; editors render straight through.
  if (isAdmin && !stats) return <div className="loader"><div className="loader-spinner" /></div>;

  const ps = stats?.previewStatus || {};
  const ready = ps.preview_ready || 0;
  const failed = ps.failed || 0;
  const aiCost = stats?.aiCostUsd || 0;

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h1 className="page-title">{t.adminDashboard}</h1>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={showTest} onChange={(e) => { setShowTest(e.target.checked); setPage(0); }} />
          <span>{t.adminShowTestData}</span>
        </label>
      </div>

      <div className="admin-links" style={{ marginTop: 16, marginBottom: 28 }}>
        {/* Create a preview on a customer's behalf (WhatsApp / off-platform).
            Pre-arms the create page's customer toggle, then sends the admin to
            the catalog to pick a template. */}
        <Link
          to="/"
          className="admin-link-card is-primary"
          onClick={() => localStorage.setItem('iamhero_admin_for_customer', '1')}
        >
          <span className="admin-link-icon"><Sparkles size={20} /></span>
          {t.adminCreatePreviewForCustomer}
        </Link>
        {/* Build an external/manual book by hand (typed scenes + uploaded or
            generated images) with a linked order to track it. */}
        <Link to="/admin/stories/build" className="admin-link-card">
          <span className="admin-link-icon"><Hammer size={20} /></span>
          {t.adminBuildStoryManually}
        </Link>
        {/* Series: batches of personalized copies spun off from one base story. */}
        <Link to="/admin/series" className="admin-link-card">
          <span className="admin-link-icon"><BookOpen size={20} /></span>
          {t.adminSeries}
        </Link>
        {isAdmin && (
          <Link to="/admin/stats" className="admin-link-card">
            <span className="admin-link-icon"><BarChart3 size={20} /></span>
            {t.adminStatistics}
          </Link>
        )}
        {/* Pay-first funnel: which stories get added to the cart, by cohort. */}
        {isAdmin && (
          <Link to="/admin/add-to-carts" className="admin-link-card">
            <span className="admin-link-icon"><ShoppingCart size={20} /></span>
            {t.adminAtcTitle}
          </Link>
        )}
        <Link to="/admin/orders" className="admin-link-card">
          <span className="admin-link-icon"><ShoppingBag size={20} /></span>
          {t.adminOrders}
        </Link>
        {isAdmin && (
          <>
            <Link to="/admin/templates" className="admin-link-card">
              <span className="admin-link-icon"><LayoutTemplate size={20} /></span>
              {t.adminTemplates}
            </Link>
            <Link to="/admin/users" className="admin-link-card">
              <span className="admin-link-icon"><Users size={20} /></span>
              {t.adminUsers}
            </Link>
            <Link to="/admin/settings" className="admin-link-card">
              <span className="admin-link-icon"><Settings size={20} /></span>
              {t.adminSettings}
            </Link>
          </>
        )}
      </div>

      {isAdmin && stats && (
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <span className="admin-stat-icon"><ShoppingBag size={22} /></span>
          <span className="admin-stat-value">{stats.orderCount}</span>
          <span className="admin-stat-label">{t.adminOrders}</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-icon is-revenue"><Wallet size={22} /></span>
          <span className="admin-stat-value">{(stats.revenue / 100).toFixed(0)}{t.currency}</span>
          <span className="admin-stat-label">{t.adminRevenue}</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-icon"><Users size={22} /></span>
          <span className="admin-stat-value">{stats.userCount}</span>
          <span className="admin-stat-label">{t.adminUsers}</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-icon"><BookOpen size={22} /></span>
          <span className="admin-stat-value">{stats.storyCount}</span>
          <span className="admin-stat-label">{t.adminStories}</span>
        </div>
        {/* AI spend so far (USD, list-price estimate) */}
        <div className="admin-stat-card">
          <span className="admin-stat-icon"><Cpu size={22} /></span>
          <span className="admin-stat-value">${aiCost.toFixed(2)}</span>
          <span className="admin-stat-label">{t.adminAiCost}</span>
        </div>
        {/* Preview attempts */}
        <div className="admin-stat-card">
          <span className="admin-stat-icon"><Eye size={22} /></span>
          <span className="admin-stat-value">{stats.storyCount}</span>
          <span className="admin-stat-label">{t.adminPreviewAttempts}</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-icon is-success"><CheckCircle2 size={22} /></span>
          <span className="admin-stat-value">{ready}</span>
          <span className="admin-stat-label">{t.adminPreviewsReady}</span>
        </div>
        {/* <div className="admin-stat-card">
          <span className={`admin-stat-icon${failed > 0 ? ' is-danger' : ''}`}><XCircle size={22} /></span>
          <span className="admin-stat-value">{failed}</span>
          <span className="admin-stat-label">معاينات فشلت</span>
        </div> */}
      </div>
      )}

      {/* Recent preview attempts — monitor every "create preview" try. */}
      <section style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <h2 className="page-title" style={{ fontSize: '1.1rem' }}>{t.adminRecentPreviewAttempts}</h2>
          <input
            type="search"
            className="admin-title-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.adminSearchPlaceholder}
            dir="rtl"
            style={{ width: '100%', maxWidth: 380 }}
          />
        </div>
        {!previews ? (
          <div className="loader"><div className="loader-spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ textAlign: 'right', color: '#888' }}>
                  <th style={{ padding: '8px 10px' }}>{t.adminColChild}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColRequester}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColContact}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColStory}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColProduct}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColPrice}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColPayment}</th>
                  {/* <th style={{ padding: '8px 10px' }}>{t.adminColReminder}</th> */}
                  <th style={{ padding: '8px 10px' }}>{t.adminColStatus}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColCost}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColTime}</th>
                  <th style={{ padding: '8px 10px' }}>{t.adminColActions}</th>
                </tr>
              </thead>
              <tbody>
                {previews.items.map((p) => (
                  <tr key={p.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '8px 10px' }}>{p.childName}</td>
                    <td style={{ padding: '8px 10px' }}>{p.contactName || '—'}</td>
                    <td style={{ padding: '8px 10px', direction: 'ltr', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {p.contactEmail || p.contactPhone ? (
                        <>
                          {p.contactEmail && (
                            <a href={`mailto:${p.contactEmail}`} className="admin-link" style={{ color: '#6366f1' }}>{p.contactEmail}</a>
                          )}
                          {p.contactPhone && (
                            <div><a href={`tel:${p.contactPhone}`} style={{ color: '#444', textDecoration: 'none' }}>{p.contactPhone}</a></div>
                          )}
                        </>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {p.coverImageUrl ? (
                          <img
                            src={resolveAsset(p.coverImageUrl)}
                            alt=""
                            style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0, background: '#f3f4f6' }}
                          />
                        ) : null}
                        <span>{p.template}</span>
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {p.productNameAr || VARIANT_AR[p.variantType] || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {p.priceCents != null ? `${(p.priceCents / 100).toFixed(0)}${t.currency}` : '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 999, fontSize: '0.8rem', whiteSpace: 'nowrap',
                        background: p.paid ? '#dcfce7' : '#f3f4f6',
                        color: p.paid ? '#166534' : '#6b7280',
                      }}>{p.paid ? t.adminPaid : t.adminUnpaid}</span>
                    </td>
                    {/* Abandoned-preview teaser email (the illustrated mid-scene
                        page + checkout link) — sent ~1h after an unpaid preview. */}
                    {/* <td style={{ padding: '8px 10px' }}>
                      <span
                        title={p.teaserEmailSentAt ? fmtDate(p.teaserEmailSentAt) : undefined}
                        style={{
                          padding: '2px 10px', borderRadius: 999, fontSize: '0.8rem', whiteSpace: 'nowrap',
                          background: p.teaserEmailSentAt ? '#dcfce7' : '#f3f4f6',
                          color: p.teaserEmailSentAt ? '#166534' : '#6b7280',
                        }}
                      >{p.teaserEmailSentAt ? 'أُرسل ✓' : 'لم يُرسل'}</span>
                    </td> */}
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 999, fontSize: '0.8rem',
                        background: p.status === 'failed' ? '#fee2e2' : p.status === 'preview_ready' || p.status === 'finalized' ? '#dcfce7' : '#ede9fe',
                        color: p.status === 'failed' ? '#b91c1c' : p.status === 'preview_ready' || p.status === 'finalized' ? '#166534' : '#5b21b6',
                      }}>{STATUS_AR[p.status] || p.status}</span>
                    </td>
                    <td style={{ padding: '8px 10px', direction: 'ltr', textAlign: 'right' }}>${(p.costUsd || 0).toFixed(3)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDate(p.createdAt)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
                        <Link to={`/admin/stories/${p.id}`} className="admin-link" style={{ color: '#6366f1' }}>{t.adminEditScenes}</Link>
                        {p.shareToken && (
                          <Link to={`/preview/${p.shareToken}`} className="admin-link" style={{ color: '#6366f1' }}>{t.adminView}</Link>
                        )}
                        {p.shareToken && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <a
                              href={demoUrl(p.shareToken)}
                              target="_blank"
                              rel="noreferrer"
                              className="admin-link"
                              style={{ color: '#0ea5e9' }}
                            >{t.adminDemo}</a>
                            <button
                              type="button"
                              className="admin-link-btn"
                              title={t.adminCopyDemoLink}
                              onClick={() => copyDemoLink(p.shareToken)}
                              style={{ color: copiedToken === p.shareToken ? '#16a34a' : '#0ea5e9' }}
                            >{copiedToken === p.shareToken ? t.adminDemoLinkCopied : '🔗'}</button>
                          </span>
                        )}
                        {/* Full copy of the book into a new story — the dialog
                            asks what the copy is (status / test / paid). */}
                        <button
                          type="button"
                          className="admin-link-btn"
                          style={{ color: '#6366f1' }}
                          onClick={() => setDuplicating({
                            id: p.id,
                            label: `${p.childName} — ${p.template}`,
                            status: p.status,
                            isTest: p.isTest,
                            gender: p.gender,
                          })}
                        >
                          {t.adminDuplicate}
                        </button>
                        <button
                          type="button"
                          className="admin-link-btn admin-link-danger"
                          onClick={() => setConfirmDelete({ id: p.id, label: `${p.childName} — ${p.template}` })}
                        >
                          {t.adminDelete}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {previews.items.length === 0 && (
                  <tr><td colSpan={12} style={{ padding: '16px', textAlign: 'center', color: '#888' }}>{t.adminNoAttemptsYet}</td></tr>
                )}
              </tbody>
            </table>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              <p style={{ color: '#888', fontSize: '0.8rem', margin: 0 }}>
                {previews.total === 0
                  ? t.adminNoAttemptsYet
                  : `${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + previews.items.length} / ${previews.total}`}
                {' '}{t.adminCostEstimateNote}
              </p>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >{t.adminPrevPage}</button>
                <span style={{ color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  {page + 1} / {Math.max(1, Math.ceil(previews.total / PAGE_SIZE))}
                </span>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= previews.total}
                >{t.adminNextPage}</button>
              </div>
            </div>
          </div>
        )}
      </section>

      {duplicating && (
        <DuplicateStoryModal
          storyId={duplicating.id}
          label={duplicating.label}
          defaultStatus={duplicating.status}
          defaultIsTest={duplicating.isTest}
          defaultGender={duplicating.gender}
          onClose={() => setDuplicating(null)}
          onDone={(result) => {
            setDuplicating(null);
            // A test copy is hidden by the default filter — flip the toggle on
            // so the admin actually sees what they just created.
            if (result?.isTest && !showTest) { setShowTest(true); setPage(0); }
            else { setPage(0); loadPreviews(); }
            if (isAdmin) api.get(`/api/admin/stats${showTest ? '?includeTest=true' : ''}`).then(setStats).catch(() => {});
          }}
        />
      )}

      {confirmDelete && (
        <div className="admin-modal-overlay" onClick={() => !working && setConfirmDelete(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal-title">{t.adminDeletePreviewTitle}</h3>
            <p className="admin-modal-message">
              {t.adminDeletePreviewMessage}
            </p>
            {confirmDelete.error && <p className="admin-modal-error">{confirmDelete.error}</p>}
            <div className="admin-modal-actions">
              <button type="button" className="admin-btn-secondary" onClick={() => setConfirmDelete(null)} disabled={working}>
                {t.adminCancel}
              </button>
              <button type="button" className="admin-btn-danger" onClick={runDelete} disabled={working}>
                {working ? '…' : t.adminConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
